import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SmallButton from '../../../components/SmallButton';
import SubmitButton from '../../../components/SubmitButton';
import { axiosKK } from '../../../services/networkRequest';
import {
  BulkReorderRouteRequest,
  LogisticsDriverOption,
  LogisticsDriversResponse,
  RecomputeRouteRequest,
  RoutePhase,
  RouteStopDTO,
  RouteStopsResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { downloadCsv } from '../../../services/downloadBlob';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function formatEtaWindow(start?: string, end?: string): string {
  if (start && end) return `${start}–${end}`;
  if (start) return start;
  return '';
}

interface StopRowProps {
  stop: RouteStopDTO;
}

function SortableStop({ stop }: StopRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="route-stop" {...attributes} {...listeners}>
      <div className="route-stop__position">{stop.routePosition + 1}</div>
      <div className="route-stop__main">
        <div className="route-stop__order">Order #{stop.orderId}</div>
        <div className="route-stop__meta">
          {stop.phase}
          {formatEtaWindow(stop.etaStart, stop.etaEnd) &&
            ` · ETA ${formatEtaWindow(stop.etaStart, stop.etaEnd)}`}
        </div>
      </div>
      {stop.overridden && <span className="route-stop__badge">Manually reordered</span>}
    </div>
  );
}

export default function RoutePlanTab() {
  const [date, setDate] = useState<string>(todayISO);
  const [driverId, setDriverId] = useState<string>('');
  const [phase, setPhase] = useState<RoutePhase | ''>('');
  const [includeUnassigned, setIncludeUnassigned] = useState(true);

  const [drivers, setDrivers] = useState<LogisticsDriverOption[]>([]);
  const [stops, setStops] = useState<RouteStopDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string>();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    axiosKK
      .get<LogisticsDriversResponse>('/logistics/dashboard/partner/drivers')
      .then(({ data }) => setDrivers(data.drivers || []))
      .catch((err) => errorMessage(setNetError)(err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    setBanner(undefined);

    const params: any = { includeUnassigned };
    if (driverId) params.driverId = Number(driverId);
    if (phase) params.phase = phase;

    axiosKK
      .get<RouteStopsResponse>(`/logistics/dashboard/partner/route-plan/${date}/stops`, { params })
      .then(({ data }) => {
        if (cancelled) return;
        const sorted = (data.stops || []).slice().sort((a, b) => a.routePosition - b.routePosition);
        setStops(sorted);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date, driverId, phase, includeUnassigned]);

  const groups = React.useMemo(() => {
    const map = new Map<string, RouteStopDTO[]>();
    stops.forEach((s) => {
      const key = `${s.driverId ?? 'unassigned'}__${s.phase}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      driverId: items[0].driverId,
      phase: items[0].phase,
      items: items.slice().sort((a, b) => a.routePosition - b.routePosition),
    }));
  }, [stops]);

  const driverName = (id: number | null): string => {
    if (id == null) return 'Unassigned';
    const d = drivers.find((x) => x.id === id);
    return d ? d.name : `Driver #${id}`;
  };

  const onDragEnd = (groupDriverId: number | null, groupPhase: RoutePhase) => (
    event: DragEndEvent
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (groupDriverId == null) {
      setNetError('Cannot reorder unassigned stops — assign a driver first.');
      return;
    }

    const groupItems = stops.filter(
      (s) => s.driverId === groupDriverId && s.phase === groupPhase
    );
    const oldIndex = groupItems.findIndex((s) => s.id === active.id);
    const newIndex = groupItems.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newGroupItems = arrayMove(groupItems, oldIndex, newIndex);

    const newStops = stops.map((s) => {
      if (s.driverId !== groupDriverId || s.phase !== groupPhase) return s;
      const newPos = newGroupItems.findIndex((g) => g.id === s.id);
      return { ...s, routePosition: newPos };
    });
    setStops(newStops);

    const payload: BulkReorderRouteRequest = {
      driverId: groupDriverId,
      phase: groupPhase,
      stopIds: newGroupItems.map((s) => s.id),
    };
    setBusy(true);
    setNetError(undefined);
    axiosKK
      .put(`/logistics/dashboard/partner/route-plan/${date}/order`, payload)
      .then(() => setBanner('Route reorder saved.'))
      .catch((err) => {
        errorMessage(setNetError)(err);
        setStops(stops);
      })
      .finally(() => setBusy(false));
  };

  const recompute = (groupDriverId?: number, groupPhase?: RoutePhase) => {
    setBusy(true);
    setBanner(undefined);
    setNetError(undefined);
    const payload: RecomputeRouteRequest = {};
    if (groupDriverId != null) payload.driverId = groupDriverId;
    if (groupPhase) payload.phase = groupPhase;

    axiosKK
      .post<RouteStopsResponse>(
        `/logistics/dashboard/partner/route-plan/${date}/recompute`,
        payload
      )
      .then(({ data }) => {
        setStops((data.stops || []).slice().sort((a, b) => a.routePosition - b.routePosition));
        setBanner('Recomputed default route.');
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setBusy(false));
  };

  const lockDay = () => {
    setBusy(true);
    setBanner(undefined);
    setNetError(undefined);
    axiosKK
      .post(`/logistics/dashboard/partner/route-plan/${date}/lock`)
      .then(() => setBanner(`Locked ${date}.`))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setBusy(false));
  };

  return (
    <>
      {netError && <p className="error">{netError}</p>}
      {banner && <p className="success-banner">{banner}</p>}

      <section className="admin-route-plan">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="filter-item">
              <label>Driver</label>
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                <option value="">All drivers</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Phase</label>
              <select value={phase} onChange={(e) => setPhase(e.target.value as RoutePhase | '')}>
                <option value="">All phases</option>
                <option value="COLLECTION">Collection</option>
                <option value="DELIVERY">Delivery</option>
              </select>
            </div>
            <div className="filter-item">
              <label>
                <input
                  type="checkbox"
                  checked={includeUnassigned}
                  onChange={(e) => setIncludeUnassigned(e.target.checked)}
                />{' '}
                Include unassigned
              </label>
            </div>
            <div className="filter-item filter-item--action">
              <SubmitButton onClick={() => recompute()} disabled={busy}>
                Recompute
              </SubmitButton>
            </div>
            <div className="filter-item filter-item--action">
              <SubmitButton onClick={lockDay} disabled={busy} red>
                Lock Day
              </SubmitButton>
            </div>
            <div className="filter-item filter-item--action">
              <SmallButton
                onClick={() => {
                  const params: Record<string, unknown> = {};
                  if (driverId) params.driverId = Number(driverId);
                  if (phase) params.phase = phase;
                  downloadCsv(
                    '/logistics/dashboard/partner/route-plan',
                    `route-plan-${date}.csv`,
                    { date, ...params }
                  ).catch((err) => errorMessage(setNetError)(err));
                }}
              >
                <ArrowDownTrayIcon width={16} height={16} /> Export CSV
              </SmallButton>
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading route…</p>
        ) : groups.length === 0 ? (
          <p>No stops for this date.</p>
        ) : (
          groups.map((g) => (
            <div key={g.key} className="route-group">
              <div className="route-group__header">
                <h4>
                  {driverName(g.driverId)} · {g.phase}
                </h4>
                {g.driverId != null && (
                  <button
                    type="button"
                    className="pricing-table__remove"
                    onClick={() => recompute(g.driverId!, g.phase)}
                    disabled={busy}
                  >
                    Recompute group
                  </button>
                )}
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd(g.driverId, g.phase)}
              >
                <SortableContext
                  items={g.items.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="route-group__list">
                    {g.items.map((stop) => (
                      <SortableStop key={stop.id} stop={stop} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          ))
        )}
      </section>
    </>
  );
}
